// AES-GCM 256 + PBKDF2-SHA256 (100k iterations) — ported verbatim from sea-moonlight-v4.html

const enc = new TextEncoder();
const dec = new TextDecoder();

export async function deriveKey(passphrase: string, salt: Uint8Array): Promise<CryptoKey> {
  const base = await crypto.subtle.importKey('raw', enc.encode(passphrase), 'PBKDF2', false, [
    'deriveKey',
  ]);
  return crypto.subtle.deriveKey(
    { name: 'PBKDF2', salt: salt as BufferSource, iterations: 100_000, hash: 'SHA-256' },
    base,
    { name: 'AES-GCM', length: 256 },
    false,
    ['encrypt', 'decrypt'],
  );
}

function b64(bytes: Uint8Array): string {
  let s = '';
  for (const x of bytes) s += String.fromCharCode(x);
  return btoa(s);
}

function unb64(s: string): Uint8Array {
  const bin = atob(s);
  const out = new Uint8Array(bin.length);
  for (let i = 0; i < bin.length; i++) out[i] = bin.charCodeAt(i);
  return out;
}

/**
 * Encrypt `plaintext` with `passphrase`.
 * Returns a packed string: `v1.<salt_b64>.<iv_b64>.<ciphertext_b64>`
 * Salt = 16 bytes random, IV = 12 bytes random, cipher = AES-GCM-256.
 */
export async function encrypt(plaintext: string, passphrase: string): Promise<string> {
  const salt = crypto.getRandomValues(new Uint8Array(16));
  const iv = crypto.getRandomValues(new Uint8Array(12));
  const key = await deriveKey(passphrase, salt);
  const ct = new Uint8Array(
    await crypto.subtle.encrypt({ name: 'AES-GCM', iv }, key, enc.encode(plaintext)),
  );
  return `v1.${b64(salt)}.${b64(iv)}.${b64(ct)}`;
}

/**
 * Encrypt `plaintext` with a pre-derived key and explicit salt+iv.
 * Returns the same packed format as `encrypt`: `v1.<salt_b64>.<iv_b64>.<ciphertext_b64>`
 * Use when sharing a single derived key across multiple encrypt calls (e.g. saveAllKeys).
 */
export async function encryptWithKey(
  plaintext: string,
  key: CryptoKey,
  salt: Uint8Array,
  iv: Uint8Array,
): Promise<string> {
  const ct = new Uint8Array(
    // Uint8Array<ArrayBufferLike> isn't directly assignable to BufferSource in strict lib.dom —
    // the intermediate cast through unknown is required to satisfy the type checker.
    await crypto.subtle.encrypt(
      { name: 'AES-GCM', iv: iv as unknown as BufferSource },
      key,
      enc.encode(plaintext),
    ),
  );
  return `v1.${b64(salt)}.${b64(iv)}.${b64(ct)}`;
}

/**
 * Decrypt a blob produced by `encrypt`.
 * Throws if the passphrase is wrong or the blob is malformed.
 */
export async function decrypt(packed: string, passphrase: string): Promise<string> {
  const [ver, s, i, c] = packed.split('.');
  if (ver !== 'v1') throw new Error('unknown cipher version');
  if (!s || !i || !c) throw new Error('malformed cipher blob');
  const key = await deriveKey(passphrase, unb64(s));
  const pt = await crypto.subtle.decrypt(
    // Uint8Array<ArrayBufferLike> isn't directly assignable to BufferSource in strict lib.dom —
    // the intermediate cast through unknown is required to satisfy the type checker.
    { name: 'AES-GCM', iv: unb64(i) as unknown as BufferSource },
    key,
    unb64(c) as unknown as BufferSource,
  );
  return dec.decode(pt);
}

/**
 * Convenience: encrypt a flat key→value record to a single JSON blob.
 * Used for batch-encrypting all API keys together.
 */
export async function encryptKeys(
  plain: Record<string, string>,
  passphrase: string,
): Promise<string> {
  return encrypt(JSON.stringify(plain), passphrase);
}

/**
 * Convenience: decrypt and parse a record blob produced by `encryptKeys`.
 */
export async function decryptKeys(
  blob: string,
  passphrase: string,
): Promise<Record<string, string>> {
  const json = await decrypt(blob, passphrase);
  return JSON.parse(json) as Record<string, string>;
}
